import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import {first} from 'lodash';
import {DateTime} from 'luxon';

import {formatDateTime, TimeFormatsEnum} from '../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {filterValidDateTime} from '../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {Localizer} from '../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {CustomFieldAttribute} from '../../libs/shared/core/src/models/api/customFieldAttributeModel';
import {TeammateModel} from '../../libs/shared/core/src/models/api/teammateModel';
import {makeEnumTypeGuard} from '../../libs/shared/core/src/models/api/types/enumTypes';
import {buildTeammateDisplayName} from '../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {ResourceMap} from '../../libs/shared/core/src/store/commonAppTypes';
import {compareName} from '../../srcCommon/helpers/localeHelpers';
import {buildCustomFieldLabel} from '../../srcCommon/helpers/models/customFieldHelpers';
import {CardModel} from '../models/api/cardModel';
import {
  CustomAttributeModel,
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
} from '../models/api/customFieldModel';
import {VariableInterpolationMap} from '../services/variableInterpolation/variableInterpolator';
import {makeVariableNameFromCustomField} from '../services/variableInterpolation/variables/customFieldVariables';

/*
 * Constants.
 */

// e.g. '12/15/20, 6:00 PM PST' or '12/15/20, 18:00 PST' depending on timeFormat.
const customFieldDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short',
};

export const isCustomFieldEnumType = makeEnumTypeGuard(CustomFieldType);

// Custom field types available during inline creation (create a new custom field while mapping columns from a CSV file).
// The enum type is missing because we do not support inline creation of enums.
export const customFieldTypes: ReadonlyArray<CustomFieldType> = [
  CustomFieldType.DATETIME,
  CustomFieldType.NUMBER,
  CustomFieldType.TEAMMATE,
  CustomFieldType.STRING,
  CustomFieldType.ENUM,
  CustomFieldType.BOOLEAN,
  CustomFieldType.INBOX,
];

export const maxNumberCustomFields = 50;

/*
 * Exports.
 */

/** The custom field values for variable interpolation are drawn from the various cards
 *  for a recipient, and which card is used is dependent on the draftNamespace.
 *
 *  Currently this means the namespace of the FROM recipient is used (e.g. private channel gets private card values)
 *  and the global card values, if any.
 */
export function buildVariablesFromRecipientCustomFields(
  recipientCard: CardModel | undefined,
  customFields: ResourceMap<CustomFieldModel>,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
): VariableInterpolationMap {
  if (!customFields) {
    return {};
  }

  const customFieldMap: VariableInterpolationMap = {};

  const recipientCustomFieldValues = recipientCard?.customFieldAttributes || [];

  // TODO: Use `interpolateCustomAttributes`.

  recipientCustomFieldValues.forEach((customFieldAttribute: CustomFieldAttribute) => {
    const customFieldModel: CustomFieldModel | undefined = customFields[customFieldAttribute.id];
    if (!customFieldModel) {
      return;
    }

    const interpolatedValue = renderCustomFieldDisplayValue(
      customFieldModel,
      customFieldAttribute.value,
      localizer,
      timeFormat,
    );

    customFieldMap[`recipient.${customFieldModel.nameSlug}`] = interpolatedValue;

    customFieldMap[makeVariableNameFromCustomField(customFieldModel)] = interpolatedValue;
  });

  return customFieldMap;
}

export function interpolateCustomAttributes(
  customFields: ResourceMap<CustomFieldModel>,
  customAttributes: ReadonlyArray<CustomFieldAttribute> | undefined,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
) {
  const variablesMap: VariableInterpolationMap = {};

  if (!customAttributes?.length) {
    return variablesMap;
  }

  for (const attribute of customAttributes) {
    const customField = customFields[attribute.id];
    if (!customField) {
      continue;
    }

    const interpolatedValue = renderCustomFieldDisplayValue(
      customField,
      attribute.value,
      localizer,
      timeFormat,
    );

    // Legacy recipent variables support.
    if (customField.resourceType === CustomFieldResourceTypesEnum.CARD) {
      variablesMap[`recipient.${customField.nameSlug}`] = interpolatedValue;
    }

    variablesMap[makeVariableNameFromCustomField(customField)] = interpolatedValue;
  }

  return variablesMap;
}

/*
 * Helpers.
 */

function renderCustomFieldDisplayValue(
  customFieldModel: CustomFieldModel,
  customFieldValue: string,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
): string | undefined {
  switch (customFieldModel.type) {
    case CustomFieldType.ENUM:
      return buildCustomFieldLabel(customFieldValue, customFieldModel);

    case CustomFieldType.DATETIME:
      return renderCustomFieldDateTimeValue(customFieldValue, localizer, timeFormat);

    default:
      return customFieldValue;
  }
}

function renderCustomFieldDateTimeValue(
  unixEpochMillisAsString: string,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
): string | undefined {
  const unixEpochMillis: number = Number(unixEpochMillisAsString);
  if (!isValidUnixEpochTimestamp(unixEpochMillis)) {
    return undefined;
  }

  const dateTime = DateTime.fromMillis(unixEpochMillis);
  return formatDateTime(localizer, dateTime, customFieldDateTimeFormatOptions, timeFormat);
}

function isValidUnixEpochTimestamp(unixEpochMillis: number) {
  return unixEpochMillis >= 0;
}

export function generateCopyValue(
  customAttribute: CustomAttributeModel,
  teammates: ReadonlyArray<TeammateModel>,
): string {
  const {value, customField} = customAttribute;
  switch (customField.type) {
    case CustomFieldType.DATETIME:
      return filterValidDateTime(DateTime.fromMillis(Number(value)))?.toISO() ?? value;

    case CustomFieldType.TEAMMATE: {
      const teammate = teammates.find((tm) => tm.email === value);
      if (!teammate) {
        return value;
      }
      return `${buildTeammateDisplayName(teammate)} (${value})`;
    }

    default:
      return value;
  }
}

export function sortCustomAttributes(customAttributes: ReadonlyArray<CustomAttributeModel>) {
  // Copy to avoid mutating array.
  return [...customAttributes].sort((customAttribute1, customAttribute2) =>
    compareName(customAttribute1.customField.name, customAttribute2.customField.name),
  );
}

export function buildDefaultCustomAttribute(customField: CustomFieldModel): CustomFieldAttribute {
  return {id: customField.id, value: buildDefaultCustomAttributeValue(customField)};
}

function buildDefaultCustomAttributeValue(customField: CustomFieldModel): string {
  switch (customField.type) {
    case CustomFieldType.BOOLEAN:
      return 'true';

    case CustomFieldType.ENUM:
      return first(customField?.enumValues)?.value ?? '';

    case CustomFieldType.NUMBER:
      return '0';

    default:
      return '';
  }
}
